import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { apiFetch } from '../utils/api';
import RequestList from './provider-request/RequestList';
import RequestForm from './provider-request/RequestForm';
import RequestDetail from './provider-request/RequestDetail';

/** Purpose: Executes { authorizations = [] }. Inputs: { authorizations = [] }. Output: Operation result. */
export default function ProviderRequest({ authorizations = [] }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [editingRequest, setEditingRequest] = useState(null);
  const [view, setView] = useState('list'); // list, form, detail

  useEffect(() => {
    loadRequests();
  }, []);

  /** Purpose: Loads requests. Inputs: none. Output: Promise with operation result. */
  const loadRequests = async () => {
    try {
      const response = await apiFetch('/provider-requests');
      if (response.ok) {
        const data = await response.json();
        setRequests(data.data || []);
      }
    } catch (err) {
      console.error('Failed to load requests:', err);
    } finally {
      setLoading(false);
    }
  };

  /** Purpose: Handles request created. Inputs: request. Output: Operation result. */
  const handleRequestCreated = (request) => {
    setRequests(prev => [request, ...prev]);
    setView('detail');
    setSelectedRequest(request);
    setEditingRequest(null);
    sessionStorage.setItem('medcaddy:provider-request-just-created', request.id);
  };

  /** Purpose: Handles request updated. Inputs: updated. Output: Operation result. */
  const handleRequestUpdated = (updated) => {
    setRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
    setSelectedRequest(updated);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {view === 'list' && (
        <RequestList
          requests={requests}
          onNewRequest={() => {
            setEditingRequest(null);
            setView('form');
          }}
          onSelectRequest={(req) => {
            setSelectedRequest(req);
            setView('detail');
          }}
        />
      )}

      {view === 'form' && (
        <RequestForm
          onBack={() => {
            setEditingRequest(null);
            setView('list');
          }}
          onCreated={handleRequestCreated}
          onUpdated={handleRequestUpdated}
          onEditComplete={(request) => {
            setEditingRequest(null);
            setSelectedRequest(request);
            setView('detail');
          }}
          initialData={editingRequest}
        />
      )}

      {view === 'detail' && selectedRequest && (
        <RequestDetail
          request={selectedRequest}
          authorizations={authorizations}
          onBack={() => {
            setView('list');
            setSelectedRequest(null);
          }}
          onUpdated={handleRequestUpdated}
          onDeleted={(requestId) => {
            setRequests(prev => prev.filter(r => r.id !== requestId));
            setView('list');
            setSelectedRequest(null);
          }}
          onEdit={() => {
            setEditingRequest(selectedRequest);
            setView('form');
          }}
          onRefresh={loadRequests}
        />
      )}
    </div>
  );
}
